/**
 * Horse Form Modal Component
 * Modal for adding/editing a horse
 */
import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Slider,
  Typography,
  FormControlLabel,
  Switch,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { Horse, HorseFormData, HorseColor, HorseSex, Player } from "./types";
import HorseColorPicker from "./HorseColorPicker";
import HorseIcon from "./HorseIcon";

interface HorseFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: HorseFormData) => void;
  horse?: Horse | null; // If provided, editing mode
  devPort: string;
  clubId: number;
  isPlayerOwned?: boolean; // If true, hide owner selection (for player adding own horse)
  playerId?: string | number; // The player adding/editing their own horse
}

function HorseFormModal({
  open,
  onClose,
  onSave,
  horse,
  devPort,
  clubId,
  isPlayerOwned = false,
  playerId,
}: HorseFormModalProps) {
  const [name, setName] = useState("");
  const [gameChukkers, setGameChukkers] = useState(2);
  const [color, setColor] = useState<HorseColor>("Bay");
  const [sex, setSex] = useState<HorseSex>("gelding");
  const [birthdate, setBirthdate] = useState<Dayjs | null>(null);
  const [ownerType, setOwnerType] = useState<"club" | "player">("club");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [status, setStatus] = useState<"available" | "unavailable">("available");
  const [notes, setNotes] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  // Load club players for owner selection
  const loadPlayers = useCallback(() => {
    setLoadingPlayers(true);
    fetch(devPort + "player_list/" + clubId + "/False")
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          const formattedPlayers = data.map((player: any[]) => ({
            player_id: player[4],
            first_name: player[0],
            last_name: player[1],
          }));
          setPlayers(formattedPlayers);
        }
        setLoadingPlayers(false);
      })
      .catch(() => setLoadingPlayers(false));
  }, [devPort, clubId]);

  useEffect(() => {
    if (open) {
      loadPlayers();
    }
  }, [open, loadPlayers]);

  // Initialize form with horse data if editing
  useEffect(() => {
    if (horse) {
      setName(horse.name);
      setGameChukkers(horse.game_chukkers);
      setColor(horse.color as HorseColor);
      setSex(horse.sex || "gelding");
      setBirthdate(horse.birthdate ? dayjs(horse.birthdate) : null);
      setOwnerType(horse.owner_type);
      setOwnerId(horse.owner_id);
      setStatus(horse.status);
      setNotes(horse.notes || "");
    } else {
      // Reset form for new horse
      setName("");
      setGameChukkers(2);
      setColor("Bay");
      setSex("gelding");
      setBirthdate(null);
      setOwnerType("club");
      setOwnerId(null);
      setStatus("available");
      setNotes("");
    }
  }, [horse, open]);

  const handleSave = () => {
    if (!name.trim()) {
      alert("Please enter a horse name");
      return;
    }

    onSave({
      name: name.trim(),
      game_chukkers: gameChukkers,
      color,
      sex,
      birthdate: birthdate ? birthdate.format("YYYY-MM-DD") : null,
      owner_type: ownerType,
      owner_id: ownerType === "club" ? null : ownerId,
      status,
      notes: notes.trim(),
    });

    onClose();
  };

  const selectedPlayer = players.find(
    (p) => String(p.player_id) === String(ownerId)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <HorseIcon color={color} size={40} />
          <span>{horse ? "Edit Horse" : "Add New Horse"}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Horse Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <HorseColorPicker value={color} onChange={setColor} />

          <FormControl fullWidth>
            <InputLabel>Sex</InputLabel>
            <Select
              value={sex}
              label="Sex"
              onChange={(e) => setSex(e.target.value as HorseSex)}
            >
              <MenuItem value="mare">Mare</MenuItem>
              <MenuItem value="gelding">Gelding</MenuItem>
              <MenuItem value="stallion">Stallion</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label="Birthdate (optional)"
            value={birthdate}
            onChange={(newValue) => setBirthdate(newValue)}
            disableFuture
            slotProps={{
              field: { clearable: true },
              textField: {
                fullWidth: true,
                helperText: "Used to display the horse's age. Leave blank if unknown.",
              },
            }}
          />

          <Stack spacing={1}>
            <Typography gutterBottom>
              Max Chukkers Per Game: {gameChukkers}
            </Typography>
            <Slider
              value={gameChukkers}
              onChange={(_, value) => setGameChukkers(value as number)}
              min={1}
              max={3}
              step={1}
              marks
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary">
              How many chukkers can this horse play in a single game?
            </Typography>
          </Stack>

          {/* Only show owner selection for managers adding club horses */}
          {!isPlayerOwned && (
            <>
              <FormControl fullWidth>
                <InputLabel>Owner Type</InputLabel>
                <Select
                  value={ownerType}
                  label="Owner Type"
                  onChange={(e) => setOwnerType(e.target.value as "club" | "player")}
                >
                  <MenuItem value="club">Club Owned</MenuItem>
                  <MenuItem value="player">Player Owned</MenuItem>
                </Select>
              </FormControl>

              {ownerType === "player" && (
                <Autocomplete
                  options={players}
                  loading={loadingPlayers}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  value={selectedPlayer || null}
                  onChange={(_, newValue) => {
                    setOwnerId(newValue ? String(newValue.player_id) : null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Owner" placeholder="Select owner" />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    String(option.player_id) === String(value.player_id)
                  }
                />
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={status === "available"}
                onChange={(e) =>
                  setStatus(e.target.checked ? "available" : "unavailable")
                }
                color="success"
              />
            }
            label={status === "available" ? "Available" : "Unavailable"}
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Any additional notes about this horse..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {horse ? "Update" : "Add Horse"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default HorseFormModal;

